import React, { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCw, Home, Copy, Check, ChevronDown, ChevronUp } from "lucide-react";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
  copied: boolean;
  showDetails: boolean;
}

/**
 * Global Error Boundary component with Sentry integration.
 * Catches runtime errors in the component tree and provides
 * a user-friendly fallback UI with error reporting.
 */
class GlobalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
      copied: false,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Structured log — error object + component stack only (no PII).
    logger.error("GlobalErrorBoundary caught an error", error, {
      componentStack: errorInfo.componentStack,
    });

    // Report to Sentry and capture event ID for reference
    const eventId = Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });

    this.setState({
      errorInfo,
      eventId,
    });
  }

  handleReload = (): void => {
    window.location.href = window.location.pathname + "?t=" + Date.now();
  };

  handleGoHome = (): void => {
    window.location.href = "/?t=" + Date.now();
  };

  handleCopyError = async (): Promise<void> => {
    const { error, errorInfo, eventId } = this.state;

    const errorText = [
      `Error: ${error?.message || "Unknown error"}`,
      `Event ID: ${eventId || "N/A"}`,
      `Timestamp: ${new Date().toISOString()}`,
      `URL: ${window.location.href}`,
      `User Agent: ${navigator.userAgent}`,
      "",
      "Stack Trace:",
      error?.stack || "No stack trace available",
      "",
      "Component Stack:",
      errorInfo?.componentStack || "No component stack available",
    ].join("\n");

    try {
      await navigator.clipboard.writeText(errorText);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch (err) {
      logger.error("Failed to copy error details", err);
    }
  };

  toggleDetails = (): void => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  render(): ReactNode {
    const { hasError, error, errorInfo, eventId, copied, showDetails } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      const isProduction = import.meta.env.PROD;

      return (
        <div
          data-testid="global-error-boundary"
          role="alert"
          className="min-h-screen flex items-center justify-center bg-background p-4"
        >
          <Card className="w-full max-w-lg shadow-lg">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-8 w-8 text-destructive" />
              </div>
              <CardTitle className="text-xl">Ein unerwarteter Fehler ist aufgetreten</CardTitle>
              <CardDescription className="mt-2">
                Die Anwendung ist auf ein Problem gestoßen. Bitte versuchen Sie, die Seite neu zu
                laden.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Error message (always show in dev, truncated in prod) */}
              {error && (
                <div className="rounded-md bg-muted p-3">
                  <p className="text-sm font-medium text-muted-foreground">
                    {isProduction ? "Fehler-Details wurden automatisch gemeldet." : error.message}
                  </p>
                </div>
              )}

              {/* Event ID for support reference */}
              {eventId && (
                <div className="flex items-center justify-between rounded-md bg-muted/50 p-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Fehler-Referenz</p>
                    <p className="font-mono text-sm">{eventId}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={this.handleCopyError}
                    className="shrink-0 h-8 w-8 p-0"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-primary" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              )}

              {/* Technical details (dev mode only) */}
              {!isProduction && errorInfo && (
                <div>
                  <button
                    onClick={this.toggleDetails}
                    className="flex w-full items-center justify-between py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <span>Technische Details</span>
                    {showDetails ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>

                  {showDetails && (
                    <div className="mt-2 max-h-48 overflow-auto rounded-md bg-muted p-3">
                      <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-words">
                        {error?.stack}
                      </pre>
                      <div className="mt-2 border-t border-border pt-2">
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          Component Stack:
                        </p>
                        <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-words">
                          {errorInfo.componentStack}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>

            <CardFooter className="flex gap-2 pt-2">
              <Button variant="outline" onClick={this.handleGoHome} className="flex-1">
                <Home className="mr-2 h-4 w-4" />
                Zur Startseite
              </Button>
              <Button onClick={this.handleReload} className="flex-1">
                <RefreshCw className="mr-2 h-4 w-4" />
                Seite neu laden
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }

    return children;
  }
}

export default GlobalErrorBoundary;
